import { apiv1 as api } from '@/adapters';
import type {
  ClientModel,
  AgentDownloadsResponse,
  AgentFleet,
  CreateClientResult,
  RotateClientSecretResult,
  RunningConnectorInvocationsPage,
} from '@/adapters/apiv1.g';
import {
  extractBoolean,
  extractClient,
  extractAgentDownloads,
  extractAgentFleet,
  extractRunningInvocations,
  unwrapData,
} from '@/adapters/shared';
import type {
  Client,
  AgentDownloads,
  AgentFleetView,
  CreateClientPayload,
  CreateClientResult as CreateClientResultType,
  RotateClientSecretResult as RotateClientSecretResultType,
  RunningInvocations,
} from '@/types/core/client';
import type { Page } from '@/types/shared/page';
import { type UUID, newUUID } from '@/utils/uuid';

export const clientService = {
  listClients: async (
    tenantId: UUID,
    opts?: { includeRevoked?: boolean }
  ): Promise<Page<Client>> => {
    const res = await api.listClients(
      tenantId,
      opts?.includeRevoked ? { include_revoked: true } : undefined
    );
    const raw = unwrapData<ClientModel[]>(res);
    const items = (raw ?? []).map(extractClient);
    return { items, next: null, prev: null };
  },

  findClient: async (
    tenantId: UUID,
    clientId: UUID
  ): Promise<Client | null> => {
    const res = await api.findClient(tenantId, clientId);
    const model = unwrapData<ClientModel>(res);
    if (!model) return null;
    return extractClient(model);
  },

  createClient: async (
    tenantId: UUID,
    payload: CreateClientPayload
  ): Promise<CreateClientResultType> => {
    const clientId = newUUID();
    const expiresAt =
      payload.expiresAt != null && payload.expiresAt !== ''
        ? new Date(payload.expiresAt).toISOString()
        : new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString();

    const res = await api.createClient(tenantId, {
      client_id: clientId,
      name: payload.name,
      description: payload.description ?? '',
      role_ids: (payload.roleIds ?? []).map(String),
      expires_at: expiresAt,
    });

    const resultData = unwrapData<CreateClientResult>(res);
    if (!resultData) {
      throw new Error('No data returned from client creation');
    }

    return {
      clientId: (resultData.client_id ?? clientId) as UUID,
      clientSecret: resultData.client_secret ?? '',
    };
  },

  removeClient: async (tenantId: UUID, clientId: UUID): Promise<boolean> => {
    const res = await api.removeClient(tenantId, clientId);
    return extractBoolean(res);
  },

  revokeClient: async (tenantId: UUID, clientId: UUID): Promise<boolean> => {
    const res = await api.disableClient(tenantId, clientId);
    return extractBoolean(res);
  },

  enableClient: async (tenantId: UUID, clientId: UUID): Promise<boolean> => {
    const res = await api.enableClient(tenantId, clientId);
    return extractBoolean(res);
  },

  // Set an agent's full site (capability) set via the register endpoint. The
  // backend reconciles to this exact set; a connected agent adopts it live.
  registerClient: async (
    tenantId: UUID,
    clientId: UUID,
    capabilities: string[]
  ): Promise<boolean> => {
    const res = await api.registerClient(tenantId, clientId, { capabilities });
    return extractBoolean(res);
  },

  rotateClientSecret: async (
    tenantId: UUID,
    clientId: UUID
  ): Promise<RotateClientSecretResultType> => {
    const res = await api.rotateClientSecret(tenantId, clientId);
    const resultData = unwrapData<RotateClientSecretResult>(res);
    if (!resultData) {
      throw new Error('No data returned from client secret rotation');
    }
    return {
      clientId,
      clientSecret: resultData.client_secret ?? '',
    };
  },

  listAgentDownloads: async (tenantId: UUID): Promise<AgentDownloads> => {
    const res = await api.listAgentDownloads(tenantId);
    const raw = unwrapData<AgentDownloadsResponse>(res);
    if (!raw) return { agent: [], agentd: [] };
    return extractAgentDownloads(raw);
  },

  listClientPresence: async (tenantId: UUID): Promise<AgentFleetView> => {
    const res = await api.listClientPresence(tenantId);
    const raw = unwrapData<AgentFleet>(res);
    if (!raw) return { settled: false, agents: [] };
    return extractAgentFleet(raw);
  },

  listRunningInvocations: async (
    tenantId: UUID
  ): Promise<RunningInvocations> => {
    const res = await api.listRunningConnectorInvocations(tenantId);
    const raw = unwrapData<RunningConnectorInvocationsPage>(res);
    if (!raw) return { invocations: [], truncated: false };
    return extractRunningInvocations(raw);
  },
};
